import { useEffect, useRef, useState } from "react";
import RegisterLink from "../../../components/RegisterLink";

const particles = Array.from({ length: 28 }, (_, i) => ({
  id: i,
  left: `${Math.random() * 100}%`,
  top: `${40 + Math.random() * 50}%`,
  size: Math.random() * 3 + 1,
  delay: `${Math.random() * 6}s`,
  duration: `${5 + Math.random() * 6}s`,
  opacity: Math.random() * 0.6 + 0.2,
}));

export default function HeroBanner() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const dots: { x: number; y: number; vx: number; vy: number; r: number; alpha: number }[] = [];
    for (let i = 0; i < 60; i++) {
      dots.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        r: Math.random() * 1.5 + 0.5,
        alpha: Math.random() * 0.5 + 0.1,
      });
    }

    let raf: number;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      dots.forEach((d) => {
        d.x += d.vx;
        d.y += d.vy;
        if (d.x < 0 || d.x > canvas.width) d.vx *= -1;
        if (d.y < 0 || d.y > canvas.height) d.vy *= -1;
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(16,92,232,${d.alpha})`;
        ctx.fill();
      });
      for (let i = 0; i < dots.length; i++) {
        for (let j = i + 1; j < dots.length; j++) {
          const dx = dots[i].x - dots[j].x;
          const dy = dots[i].y - dots[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(dots[i].x, dots[i].y);
            ctx.lineTo(dots[j].x, dots[j].y);
            ctx.strokeStyle = `rgba(16,92,232,${0.15 * (1 - dist / 100)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <section
      id="about"
      className="relative w-full overflow-hidden noise-overlay"
      style={{
        minHeight: "100vh",
        background: "linear-gradient(160deg, #03112B 0%, #041840 25%, #03112B 55%, #03112B 100%)",
      }}
    >
      {/* Multi-layer radial glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute animate-breathe"
          style={{
            width: "700px",
            height: "700px",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -60%)",
            background: "radial-gradient(circle, rgba(16,92,232,0.22) 0%, transparent 70%)",
            borderRadius: "50%",
          }}
        />
        <div
          className="absolute animate-breathe"
          style={{
            width: "500px",
            height: "500px",
            top: "40%",
            left: "30%",
            transform: "translate(-50%, -50%)",
            background: "radial-gradient(circle, rgba(16,92,232,0.15) 0%, transparent 70%)",
            borderRadius: "50%",
            animationDelay: "1.5s",
          }}
        />
        <div
          className="absolute"
          style={{
            width: "300px",
            height: "300px",
            top: "55%",
            right: "20%",
            background: "radial-gradient(circle, rgba(224,201,127,0.1) 0%, transparent 70%)",
            borderRadius: "50%",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background: "radial-gradient(ellipse at center, transparent 40%, rgba(8,30,102,0.7) 100%)",
          }}
        />
      </div>

      {/* Light streaks */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute animate-streak-1"
          style={{
            width: "60%",
            height: "1px",
            top: "35%",
            left: 0,
            background: "linear-gradient(90deg, transparent, rgba(16,92,232,0.5), transparent)",
          }}
        />
        <div
          className="absolute animate-streak-2"
          style={{
            width: "40%",
            height: "1px",
            top: "55%",
            left: 0,
            background: "linear-gradient(90deg, transparent, rgba(224,201,127,0.35), transparent)",
          }}
        />
        <div
          className="absolute animate-streak-3"
          style={{
            width: "50%",
            height: "1px",
            top: "70%",
            left: 0,
            background: "linear-gradient(90deg, transparent, rgba(16,92,232,0.4), transparent)",
          }}
        />
      </div>

      {/* Canvas particles */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ opacity: 0.7 }}
      />

      {/* Floating dot particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {particles.map((p) => (
          <div
            key={p.id}
            className="absolute rounded-full"
            style={{
              left: p.left,
              top: p.top,
              width: `${p.size}px`,
              height: `${p.size}px`,
              background:
                p.id % 3 === 0
                  ? `rgba(224,201,127,${p.opacity})`
                  : p.id % 3 === 1
                  ? `rgba(16,92,232,${p.opacity})`
                  : `rgba(77,142,245,${p.opacity})`,
              animation: `floatUp ${p.duration} ${p.delay} ease-in-out infinite`,
            }}
          />
        ))}
      </div>

      {/* Geometric ring */}
      <div
        className="absolute pointer-events-none"
        style={{ top: "50%", left: "50%", transform: "translate(-50%, -55%)" }}
      >
        <div
          className="animate-rotate-slow"
          style={{
            width: "520px",
            height: "520px",
            border: "1px solid rgba(16,92,232,0.1)",
            borderRadius: "50%",
          }}
        />
        <div
          className="absolute inset-8 animate-rotate-slow"
          style={{
            border: "1px solid rgba(224,201,127,0.06)",
            borderRadius: "50%",
            animationDirection: "reverse",
            animationDuration: "30s",
          }}
        />
      </div>

      {/* Hero Blog Content */}
      <div
        className="relative z-10 flex flex-col items-center justify-center text-center px-4"
        style={{ minHeight: "100vh", paddingTop: "80px" }}
      >
        {/* SEO Badge */}
        <div
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6 text-xs font-semibold tracking-widest uppercase"
          style={{
            background: "rgba(16,92,232,0.15)",
            border: "1px solid rgba(16,92,232,0.4)",
            color: "#7ab0f8",
            backdropFilter: "blur(8px)",
          }}
        >
          <span
            className="w-1.5 h-1.5 rounded-full animate-pulse inline-block"
            style={{ background: "#105CE8" }}
          />
          Best Online Casino Australia 2026
        </div>

        {/* Main Logo */}
        <div className="relative mb-4">
          <div
            className="absolute inset-0 animate-breathe pointer-events-none"
            style={{
              background: "radial-gradient(ellipse, rgba(16,92,232,0.4) 0%, transparent 70%)",
              filter: "blur(40px)",
              transform: "scale(1.8)",
            }}
          />
          <div
            className="absolute inset-0 animate-breathe pointer-events-none"
            style={{
              background: "radial-gradient(ellipse, rgba(59,130,246,0.25) 0%, transparent 60%)",
              filter: "blur(30px)",
              transform: "scale(2)",
              animationDelay: "1s",
            }}
          />
          <h1 className="relative">
            <a href="/" className="block mx-auto w-fit" aria-label="Ace96au home">
              <img
                src="https://static.readdy.ai/image/b513b5b4a847779ee65de1525456761b/b8500fc5b880150736c6d516c60a553d.png"
                alt="Ace96au - Australia's Best Online Casino Platform"
                className="relative w-72 md:w-96 lg:w-[480px] h-auto object-contain mx-auto"
                style={{
                  filter:
                    "drop-shadow(0 0 30px rgba(16,92,232,0.6)) drop-shadow(0 0 60px rgba(16,92,232,0.3))",
                }}
              />
            </a>
          </h1>
        </div>

        {/* H1 SEO Blog Title */}
        <h1
          className="text-2xl md:text-4xl lg:text-5xl font-black text-white mb-4 leading-tight max-w-4xl"
          style={{ fontFamily: "'Cinzel', serif" }}
        >
          <span className="gradient-text">Ace96au</span> &mdash; The Future of Online Casino Gaming in Australia
        </h1>

        {/* Blog-style subtitle / intro paragraph */}
        <p
          className="text-sm md:text-base text-slate-300 max-w-3xl mb-8 leading-relaxed"
          style={{ fontFamily: "'Inter', sans-serif", letterSpacing: "0.02em" }}
        >
          Welcome to <strong className="text-white">Ace96au</strong>, Australia&apos;s most innovative
          <strong className="text-white"> online casino platform</strong> built exclusively for
          players who demand more. With <strong className="text-white">over 4,000 games</strong> from
          world-class providers including <strong className="text-white">JILI, VPower, BNG, Spadegaming, Pragmatic Play, Evolution Gaming, CQ9 Gaming, WF Gaming, Live22, Joker, Pegasus, ACE333, 918Kaya, 918Kiss and Mega888</strong>, Ace96au delivers an unmatched gaming experience. Our platform is designed around one principle: <em className="text-slate-200">more bonuses, more choice, more fun</em>. From generous welcome packages to daily reload bonuses and weekly rebate programs, every player &mdash; new or loyal &mdash; gets real value.
        </p>

        {/* Expandable blog content (SEO rich text) */}
        <div
          className="max-w-3xl text-left mb-8 overflow-hidden transition-all duration-500"
          style={{
            maxHeight: expanded ? "800px" : "0px",
            opacity: expanded ? 1 : 0,
          }}
        >
          <div
            className="glass-card rounded-2xl p-6 md:p-8"
            style={{ background: "rgba(255,255,255,0.03)" }}
          >
            <h3
              className="text-lg font-bold text-white mb-3"
              style={{ fontFamily: "'Cinzel', serif" }}
            >
              Why Ace96au Stands Out from Other Casino Platforms
            </h3>
            <p className="text-sm text-slate-300 leading-relaxed mb-4">
              Unlike traditional online casinos that recycle the same outdated games and stingy bonus structures, Ace96au partners with <strong className="text-white">15+ premium game providers</strong> to bring fresh content every month. Our live casino suite powered by <strong className="text-white">Evolution Gaming</strong> features real-time dealers, HD streaming and immersive table environments &mdash; rivalling the best land-based venues in Melbourne and Sydney.
            </p>
            <p className="text-sm text-slate-300 leading-relaxed mb-4">
              The platform&apos;s <strong className="text-white">vibrant, modern UI</strong> is built with player experience at its core. Every button, animation and transition is tuned for speed and clarity. Whether you&apos;re spinning slots on your morning commute or placing live sports bets during the AFL Grand Final, the interface responds instantly with zero lag.
            </p>
            <p className="text-sm text-slate-300 leading-relaxed">
              Security is non-negotiable. Ace96au operates under a <strong className="text-white">PAGCOR gaming licence</strong> and employs 256-bit SSL encryption alongside independent RNG audits. Your funds and personal data are protected to the same standard used by Australia&apos;s leading financial institutions.
            </p>
          </div>
        </div>

        {/* Read More toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="inline-flex items-center gap-2 text-sm font-semibold mb-8 cursor-pointer transition-all duration-200 hover:text-white"
          style={{ color: "#7ab0f8" }}
        >
          <span>{expanded ? "Show Less" : "Read Full Review"}</span>
          <i className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
        </button>

        {/* Bonus tag */}
        <div
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full mb-10 text-sm font-bold"
          style={{
            background: "linear-gradient(135deg, rgba(224,201,127,0.15), rgba(201,168,76,0.1))",
            border: "1px solid rgba(224,201,127,0.35)",
            color: "#e0c97f",
          }}
        >
          <i className="ri-gift-line" />
          New Register FREE $160.99 &middot; VVIP Bonus 100% &middot; Special Rebate Bonus AUD 50
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center gap-4 mb-16">
          <RegisterLink
            trackLabel="hero-signup"
            className="btn-gold px-10 py-4 rounded-xl text-base font-black tracking-widest whitespace-nowrap inline-flex items-center justify-center"
            style={{ fontFamily: "'Cinzel', serif", letterSpacing: "0.15em" }}
          >
            SIGN UP NOW
          </RegisterLink>
          <RegisterLink
            trackLabel="hero-login"
            className="px-10 py-4 rounded-xl text-base font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 hover:scale-105 inline-flex items-center justify-center"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(16,92,232,0.35)",
              color: "#7ab0f8",
              backdropFilter: "blur(8px)",
              letterSpacing: "0.08em",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(16,92,232,0.7)";
              (e.currentTarget as HTMLAnchorElement).style.boxShadow =
                "0 0 20px rgba(16,92,232,0.4)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(16,92,232,0.35)";
              (e.currentTarget as HTMLAnchorElement).style.boxShadow = "none";
            }}
          >
            Log in
          </RegisterLink>
        </div>

        {/* Stats row */}
        <div className="flex flex-wrap justify-center gap-6 md:gap-14 mb-16">
          {[
            { value: "89k", label: "New Players" },
            { value: "60+", label: "Game Providers" },
            { value: "98.71%", label: "RTP Rate" },
            { value: "4000+", label: "Games" },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div
                className="text-2xl md:text-3xl font-black gradient-text-gold mb-1"
                style={{ fontFamily: "'Cinzel', serif" }}
              >
                {stat.value}
              </div>
              <div className="text-xs text-slate-400 tracking-widest uppercase">{stat.label}</div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}